/**
 * EE Scheduler 管理頁（Enterprise Edition only）
 *
 * 三個 Tab：
 *   1. 任務列表  — CRUD + 啟用切換 + 立即執行 + cron 預覽
 *   2. 執行紀錄  — 分頁、篩選、展開看 traceback
 *   3. 佇列狀態  — 目前 pending/dispatched + 統計
 *
 * 對應後端：/api/ee/scheduler/*
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tabs, Tab, Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Switch, FormControlLabel, Select, MenuItem,
  InputLabel, FormControl, IconButton, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Paper, Pagination, Collapse,
  Tooltip, Typography, Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  Preview as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import { useSystem } from '../../contexts/SystemContext';
import { getI18nValue } from '../../utils/i18nHelper';
import {
  SchedulerJob, SchedulerJobCreate, SchedulerRun, QueueItem, QueueStats,
  listJobs, createJob, updateJob, deleteJob, toggleJob, runJobNow, previewCron,
  listRuns, listQueue, getQueueStats, getStatus,
} from '../../services/eeSchedulerService';
import { formatLocal } from '../../utils/dateHelper';

// ============================================================
// Utilities
// ============================================================

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return formatLocal(iso);
  } catch {
    return iso;
  }
};

const statusChip = (status: string) => {
  const color: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
    waiting: 'default',
    running: 'info',
    success: 'success',
    failed: 'error',
    pending: 'warning',
    dispatched: 'info',
    done: 'default',
  };
  return <Chip label={status} size="small" color={color[status] || 'default'} />;
};

// ============================================================
// Tab 1: 任務列表
// ============================================================

const JobsTab: React.FC<{ availableLangs: string[] }> = ({ availableLangs }) => {
  const { i18n } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const [jobs, setJobs] = useState<SchedulerJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<SchedulerJob | null>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listJobs();
      setJobs(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const handleToggle = async (job: SchedulerJob) => {
    try {
      await toggleJob(job.id);
      // lint-msg-allow: scheduler uses inline status text pending SYS codes assignment
      showSuccess(job.is_active ? '已停用' : '已啟用');
      reload();
    } catch (err) { showApiError(err); }
  };

  const handleRunNow = async (job: SchedulerJob) => {
    try {
      const resp = await runJobNow(job.id);
      // lint-msg-allow: dynamic queue_id in message pending SYS code with params
      showSuccess(`已加入佇列（queue_id=${resp.queue_id}）`);
      reload();
    } catch (err) { showApiError(err); }
  };

  const handleDelete = async (job: SchedulerJob) => {
    if (!window.confirm(`確定刪除任務「${getI18nValue(job.job_name, i18n.language)}」？`)) return;
    try {
      await deleteJob(job.id);
      // lint-msg-allow: scheduler delete pending SYS code assignment
      showSuccess('已刪除');
      reload();
    } catch (err) { showApiError(err); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />}
                onClick={() => { setEditing(null); setEditOpen(true); }}>
          新增任務
        </Button>
        <IconButton onClick={reload} disabled={loading}><RefreshIcon /></IconButton>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>名稱</TableCell>
              <TableCell>Cron</TableCell>
              <TableCell>呼叫模組</TableCell>
              <TableCell>類別</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>連續失敗</TableCell>
              <TableCell>執行次數</TableCell>
              <TableCell>下次執行</TableCell>
              <TableCell>啟用</TableCell>
              <TableCell>動作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map(job => (
              <TableRow key={job.id}>
                <TableCell>{job.id}</TableCell>
                <TableCell>
                  <div>{getI18nValue(job.job_name, i18n.language)}</div>
                  <Typography variant="caption" color="text.secondary">
                    {job.job_code}{job.is_builtin && ' (內建)'}
                  </Typography>
                </TableCell>
                <TableCell><code>{job.cron_expr}</code></TableCell>
                <TableCell><code>{job.function_code}</code></TableCell>
                <TableCell>{job.job_type}</TableCell>
                <TableCell>{statusChip(job.status)}</TableCell>
                <TableCell>{job.consecutive_fail_count}</TableCell>
                <TableCell>{job.run_count}</TableCell>
                <TableCell>{formatDateTime(job.next_run_at)}</TableCell>
                <TableCell>
                  <Switch size="small" checked={job.is_active}
                          onChange={() => handleToggle(job)} />
                </TableCell>
                <TableCell>
                  <Tooltip title="立即執行">
                    <IconButton size="small" onClick={() => handleRunNow(job)}>
                      <PlayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="編輯">
                    <IconButton size="small" onClick={() => { setEditing(job); setEditOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="刪除（內建任務禁止）">
                    <span>
                      <IconButton size="small" disabled={job.is_builtin}
                                  onClick={() => handleDelete(job)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <JobEditDialog open={editOpen} onClose={() => setEditOpen(false)}
                     job={editing} onSaved={() => { setEditOpen(false); reload(); }}
                     availableLangs={availableLangs} />
    </Box>
  );
};

// ============================================================
// Job Edit Dialog
// ============================================================

const JobEditDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  job: SchedulerJob | null;
  onSaved: () => void;
  availableLangs: string[];
}> = ({ open, onClose, job, onSaved, availableLangs }) => {
  const { showSuccess, showApiError } = useMessage();
  const isEdit = !!job;
  const isBuiltin = job?.is_builtin ?? false;

  const [form, setForm] = useState<SchedulerJobCreate>(() => ({
    job_code: '',
    job_name: {},
    description: {},
    cron_expr: '0 * * * *',
    function_code: 'ee_demo_echo',
    params: {},
    job_type: 'exclusive',
    is_active: true,
    fail_alert_enabled: true,
    overdue_alert_enabled: true,
  }));
  const [paramsText, setParamsText] = useState('{}');
  const [preview, setPreview] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      if (job) {
        setForm({
          job_code: job.job_code,
          job_name: job.job_name,
          description: job.description || {},
          cron_expr: job.cron_expr,
          function_code: job.function_code,
          params: job.params,
          job_type: job.job_type,
          is_active: job.is_active,
          fail_alert_enabled: job.fail_alert_enabled,
          overdue_alert_enabled: job.overdue_alert_enabled,
        });
        setParamsText(JSON.stringify(job.params || {}, null, 2));
      } else {
        setForm({
          job_code: '', job_name: {}, description: {},
          cron_expr: '0 * * * *', function_code: 'ee_demo_echo',
          params: {}, job_type: 'exclusive', is_active: true,
          fail_alert_enabled: true, overdue_alert_enabled: true,
        });
        setParamsText('{}');
      }
      setPreview([]);
    }
  }, [open, job]);

  const handlePreview = async () => {
    try {
      const resp = await previewCron(form.cron_expr, 5);
      setPreview(resp.next_runs);
    } catch (err) { showApiError(err); }
  };

  const handleSave = async () => {
    let parsedParams: Record<string, any> = {};
    try {
      parsedParams = paramsText.trim() ? JSON.parse(paramsText) : {};
    } catch (e) {
      showApiError({ response: { data: { detail: 'params 不是合法的 JSON' } } });
      return;
    }
    try {
      const payload = { ...form, params: parsedParams };
      if (isEdit && job) {
        await updateJob(job.id, payload);
        // lint-msg-allow: scheduler CRUD pending SYS code assignment
        showSuccess('已更新');
      } else {
        await createJob(payload);
        // lint-msg-allow: scheduler CRUD pending SYS code assignment
        showSuccess('已建立');
      }
      onSaved();
    } catch (err) { showApiError(err); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? '編輯任務' : '新增任務'}{isBuiltin && '（內建）'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="job_code" value={form.job_code}
                     onChange={e => setForm({ ...form, job_code: e.target.value })}
                     disabled={isEdit} size="small" fullWidth />

          {availableLangs.map(lang => (
            <TextField key={lang} label={`任務名稱 (${lang})`}
                       value={form.job_name[lang] || ''}
                       onChange={e => setForm({
                         ...form, job_name: { ...form.job_name, [lang]: e.target.value }
                       })}
                       size="small" fullWidth />
          ))}

          <Divider><Typography variant="caption">排程</Typography></Divider>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField label="Cron 表達式" value={form.cron_expr}
                       onChange={e => setForm({ ...form, cron_expr: e.target.value })}
                       disabled={isBuiltin} size="small" sx={{ flex: 1 }}
                       helperText="分 時 日 月 週（最小 1 分鐘）" />
            <Button startIcon={<PreviewIcon />} onClick={handlePreview}>預覽</Button>
          </Box>
          {preview.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
              <Typography variant="caption">下 5 次觸發（UTC）：</Typography>
              {preview.map((t, i) => (
                <Typography key={i} variant="body2">
                  {i + 1}. {formatDateTime(t)}
                </Typography>
              ))}
            </Paper>
          )}

          <FormControl size="small" fullWidth disabled={isBuiltin}>
            <InputLabel>類別</InputLabel>
            <Select value={form.job_type} label="類別"
                    onChange={e => setForm({ ...form, job_type: e.target.value as any })}>
              <MenuItem value="exclusive">exclusive（不可重複）</MenuItem>
              <MenuItem value="repeatable">repeatable（可重複並行）</MenuItem>
            </Select>
          </FormControl>

          <Divider><Typography variant="caption">執行</Typography></Divider>
          <TextField label="function_code（白名單模組）" value={form.function_code}
                     onChange={e => setForm({ ...form, function_code: e.target.value })}
                     disabled={isBuiltin} size="small" fullWidth
                     helperText="必須掛在 ee_mod_automate 底下" />
          <TextField label="params（JSON）" value={paramsText}
                     onChange={e => setParamsText(e.target.value)}
                     multiline minRows={3} size="small" fullWidth />

          <Divider><Typography variant="caption">警示</Typography></Divider>
          <FormControlLabel label="連續失敗達閾值時寄失敗警示"
            control={<Switch checked={form.fail_alert_enabled}
                             onChange={e => setForm({ ...form, fail_alert_enabled: e.target.checked })} />} />
          <FormControlLabel label="長時間未執行時寄逾期警示"
            control={<Switch checked={form.overdue_alert_enabled}
                             onChange={e => setForm({ ...form, overdue_alert_enabled: e.target.checked })} />} />
          <FormControlLabel label="啟用"
            control={<Switch checked={form.is_active}
                             onChange={e => setForm({ ...form, is_active: e.target.checked })} />} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" onClick={handleSave}>儲存</Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================
// Tab 2: 執行紀錄
// ============================================================

const RunsTab: React.FC = () => {
  const { showApiError } = useMessage();
  const [runs, setRuns] = useState<SchedulerRun[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await listRuns({
        page, page_size: pageSize,
        status: statusFilter ? statusFilter as any : undefined,
      });
      setRuns(resp.items);
      setTotal(resp.total);
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [page, pageSize, statusFilter, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>狀態篩選</InputLabel>
          <Select value={statusFilter} label="狀態篩選"
                  onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">全部</MenuItem>
            <MenuItem value="success">success</MenuItem>
            <MenuItem value="failed">failed</MenuItem>
            <MenuItem value="running">running</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={reload} disabled={loading}><RefreshIcon /></IconButton>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>ID</TableCell>
              <TableCell>任務代碼</TableCell>
              <TableCell>觸發源</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>開始</TableCell>
              <TableCell>耗時</TableCell>
              <TableCell>錯誤</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map(run => (
              <React.Fragment key={run.id}>
                <TableRow>
                  <TableCell>
                    <IconButton size="small" onClick={() => setExpandedId(expandedId === run.id ? null : run.id)}>
                      {expandedId === run.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{run.id}</TableCell>
                  <TableCell><code>{run.job_code}</code></TableCell>
                  <TableCell>{run.trigger_source}</TableCell>
                  <TableCell>{statusChip(run.status)}</TableCell>
                  <TableCell>{formatDateTime(run.started_at)}</TableCell>
                  <TableCell>{run.duration_ms != null ? `${run.duration_ms}ms` : '—'}</TableCell>
                  <TableCell>{run.error_code || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                    <Collapse in={expandedId === run.id}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', my: 1 }}>
                        {run.result && (
                          <>
                            <Typography variant="caption">result:</Typography>
                            <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(run.result, null, 2)}</pre>
                          </>
                        )}
                        {run.error_message && (
                          <>
                            <Typography variant="caption" color="error">error_message:</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>{run.error_message}</Typography>
                          </>
                        )}
                        {run.error_traceback && (
                          <>
                            <Typography variant="caption" color="error">traceback:</Typography>
                            <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>{run.error_traceback}</pre>
                          </>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          host: {run.host || '—'} · queue_id: {run.queue_id ?? '—'}
                        </Typography>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination count={Math.ceil(total / pageSize)} page={page} onChange={(_, p) => setPage(p)} />
      </Box>
    </Box>
  );
};

// ============================================================
// Tab 3: 佇列狀態
// ============================================================

const QueueTab: React.FC = () => {
  const { showApiError } = useMessage();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [status, setStatusInfo] = useState<any>(null);

  const reload = useCallback(async () => {
    try {
      const [q, s, st] = await Promise.all([listQueue(), getQueueStats(), getStatus()]);
      setQueue(q); setStats(s); setStatusInfo(st);
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => {
    reload();
    const timer = setInterval(reload, 5000);
    return () => clearInterval(timer);
  }, [reload]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">服務狀態</Typography>
          <Typography variant="h6">
            {status?.running ? '運行中' : '停止'}
            {' '}· watchdog: {status?.watchdog_alive ? '✓' : '✗'}
            {' '}· trigger: {status?.trigger_alive ? '✓' : '✗'}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">待執行</Typography>
          <Typography variant="h6">{stats?.pending_count ?? '—'}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">執行中</Typography>
          <Typography variant="h6">{stats?.dispatched_count ?? '—'}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">最舊待執行</Typography>
          <Typography variant="body2">{formatDateTime(stats?.oldest_pending_at)}</Typography>
        </Paper>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Queue ID</TableCell>
              <TableCell>任務代碼</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>排定時間</TableCell>
              <TableCell>入佇時間</TableCell>
              <TableCell>取出時間</TableCell>
              <TableCell>Run ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queue.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center">佇列無待執行項目</TableCell></TableRow>
            )}
            {queue.map(q => (
              <TableRow key={q.id}>
                <TableCell>{q.id}</TableCell>
                <TableCell><code>{q.job_code}</code></TableCell>
                <TableCell>{statusChip(q.status)}</TableCell>
                <TableCell>{formatDateTime(q.scheduled_at)}</TableCell>
                <TableCell>{formatDateTime(q.enqueued_at)}</TableCell>
                <TableCell>{formatDateTime(q.dequeued_at)}</TableCell>
                <TableCell>{q.run_id ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ============================================================
// Main
// ============================================================

const EeSchedulerPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const { availableLanguages } = useSystem();
  const langs = availableLanguages.map(l => l.code);

  return (
    <Box>
      <FunctionPageHeader funcCode="ee_scheduler" fallback="排程管理" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="任務列表" />
        <Tab label="執行紀錄" />
        <Tab label="佇列狀態" />
      </Tabs>
      <Box sx={{ mt: 2 }}>
        {tab === 0 && <JobsTab availableLangs={langs} />}
        {tab === 1 && <RunsTab />}
        {tab === 2 && <QueueTab />}
      </Box>
    </Box>
  );
};

export default EeSchedulerPage;